import { useEffect, useState } from 'react'
import { useTranslation } from '@lb/i18n'
import { useSubmitFeedback } from '@lb/domain'
import { Button, IconAlert, IconHeart, IconMessage, IconStar, Modal, cn } from '@lb/ui'

import { http } from '../../lib/client'

/**
 * "Share feedback" — the rail's last item, and the mock's `FeedbackModal`.
 *
 * ── Why a modal and not the Profile section ───────────────────────────────
 * The same form already exists on `/profile`, and this is deliberately not a
 * link to it. The mock puts feedback one press away from wherever you are
 * because the moment worth capturing is the one where something just annoyed
 * you — routing that impulse through a settings screen loses it. The Profile
 * section stays; it is where somebody who went looking finds it.
 *
 * Both call `useSubmitFeedback`, so there is one transport and one contract.
 *
 * ── The rating is optional, the message is not ────────────────────────────
 * `SubmitFeedback` marks `rating` optional and `message` as at least one
 * character, and the submit button enforces exactly that. The mock enables its
 * button on a rating alone, which would post an empty message the API refuses.
 */
export function FeedbackDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [rating, setRating] = useState<number>()
  const submit = useSubmitFeedback(http)

  /*
   * Reset on close, not on open.
   *
   * Resetting on open would wipe a draft the instant a mis-click closed the
   * dialog and the member re-opened it; resetting on close means the next
   * opening is clean while an accidental Escape still costs the text. The
   * mutation is reset too — otherwise re-opening shows the thank-you panel
   * from the previous send and there is no way back to the form.
   */
  useEffect(() => {
    if (open) return
    setMessage('')
    setRating(undefined)
    submit.reset()
    // `submit` is a stable mutation object; depending on it would reset the
    // form on every render that produced a new status.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const sent = submit.isSuccess

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={sent ? t('feedback.doneTitle') : t('feedback.title')}
      hideTitle
      closeLabel={t('feedback.close')}
    >
      {sent ? (
        <div className="flex flex-col items-center gap-3 pt-2 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-success text-ink-on-tint">
            <IconHeart size={26} title={null} />
          </span>
          <h2 className="font-serif text-[21px] text-ink">{t('feedback.doneTitle')}</h2>
          <p className="text-[14px] leading-relaxed text-ink-soft">{t('feedback.doneBody')}</p>
          <Button fullWidth className="mt-1" onClick={() => onOpenChange(false)}>
            {t('feedback.close')}
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-3.5"
          onSubmit={(event) => {
            event.preventDefault()
            const trimmed = message.trim()
            if (!trimmed) return
            submit.mutate({ message: trimmed, ...(rating ? { rating } : {}) })
          }}
        >
          <span className="grid h-12 w-12 place-items-center rounded-full bg-bleu-gris-light text-ink-on-tint">
            <IconMessage size={24} title={null} />
          </span>

          <div>
            <h2 className="font-serif text-[21px] leading-tight text-ink">{t('feedback.title')}</h2>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-soft">
              {t('feedback.lede')}
            </p>
          </div>

          {/*
           * Stars, and they are radios — one choice out of five, which is what
           * `role="radiogroup"` means and what a screen reader announces. The
           * fill is the state; `aria-checked` is what carries it non-visually.
           * Pressing the current value clears it, because the rating is
           * optional and there would otherwise be no way to take it back.
           */}
          <div role="radiogroup" aria-label={t('feedback.rating')} className="flex gap-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={rating === value}
                aria-label={t('feedback.stars', { count: value })}
                onClick={() => setRating(rating === value ? undefined : value)}
                className={cn(
                  'grid h-11 w-11 place-items-center rounded-full transition-colors',
                  rating != null && value <= rating
                    ? 'text-ink'
                    : 'text-border-deep hover:text-ink/60',
                )}
              >
                <IconStar size={27} title={null} />
              </button>
            ))}
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="sr-only">{t('feedback.title')}</span>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={4}
              maxLength={5000}
              placeholder={t('feedback.placeholder')}
              className="w-full resize-y rounded-[var(--r-sm)] border border-border bg-canvas p-3 text-[14.5px] outline-none transition-colors placeholder:text-muted focus-visible:border-ink"
            />
          </label>

          {submit.isError ? (
            <p role="alert" className="text-[13px] rounded-[var(--r-sm)] flex items-start gap-2 bg-error-surface px-3 py-2 text-error">
              <IconAlert size={16} title={null} className="mt-px shrink-0" />
              {t('feedback.failed')}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={!message.trim()}
            loading={submit.isPending}
            loadingLabel={t('state.busy')}
          >
            {t('feedback.submit')}
          </Button>

          <p className="text-center text-[11.5px] text-muted">{t('feedback.note')}</p>
        </form>
      )}
    </Modal>
  )
}
